import type { ResearchDomainDefinition } from '@/shared/document-intelligence/researchOntology';

import type {
    DocumentConcept,
    DocumentConceptReference,
    DocumentIntelligence,
} from '../AttachmentRedactionEditor/documentIntelligence';
import type { ExtractedDocument, ExtractedDocumentBlock } from '../documentExtraction';

export interface DocumentConceptTextAnnotation {
    blockId: string;
    concept: DocumentConcept;
    domain?: ResearchDomainDefinition;
    end: number;
    reference: DocumentConceptReference;
    start: number;
}

interface ReferenceCandidate {
    block: ExtractedDocumentBlock;
    blockIndex: number;
    end: number;
    sourceStart?: number;
    start: number;
}

const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getReferenceCandidates = (
    blocks: ExtractedDocumentBlock[],
    reference: DocumentConceptReference,
): ReferenceCandidate[] => {
    const expression = new RegExp(reference.text.trim().split(/\s+/).map(escapePattern).join('\\s+'), 'giu');

    return blocks.flatMap((block, blockIndex) => {
        if (block.page !== reference.page) {
            return [];
        }

        return [...block.text.matchAll(expression)].map((match): ReferenceCandidate => {
            const start = match.index;
            const end = start + match[0].length;
            const segment = block.segments.find(
                (candidate) => candidate.blockStart <= start && candidate.blockEnd >= end,
            );
            const sourceStart = segment
                ? segment.sourceStart +
                  ((start - segment.blockStart) / Math.max(segment.blockEnd - segment.blockStart, 1)) *
                      Math.max(segment.sourceEnd - segment.sourceStart, 0)
                : undefined;

            return { block, blockIndex, end, sourceStart, start };
        });
    });
};

const pageHasStableSourceOffsets = (blocks: ExtractedDocumentBlock[], page: number) => {
    const pageBlocks = blocks.filter((block) => block.page === page);

    return (
        pageBlocks.length === 1 || pageBlocks.some((block) => block.segments.some((segment) => segment.sourceStart > 0))
    );
};

const overlaps = (left: DocumentConceptTextAnnotation, right: DocumentConceptTextAnnotation) =>
    left.start < right.end && left.end > right.start;

const resolveOverlappingAnnotations = (annotations: DocumentConceptTextAnnotation[]) => {
    const resolved: DocumentConceptTextAnnotation[] = [];

    annotations
        .sort((left, right) => left.start - right.start || right.end - right.start - (left.end - left.start))
        .forEach((annotation) => {
            const overlappingIndex = resolved.findIndex((candidate) => overlaps(candidate, annotation));

            if (overlappingIndex < 0) {
                resolved.push(annotation);
                return;
            }

            const current = resolved[overlappingIndex];
            if (annotation.end - annotation.start > current.end - current.start) {
                resolved[overlappingIndex] = annotation;
            }
        });

    return resolved.sort((left, right) => left.start - right.start);
};

/**
 * Projects page-level ontology references onto normalized semantic blocks.
 * Source offsets are preferred for PDFs; normalized DOCX/text documents use
 * deterministic reference order because their rendered whitespace differs.
 */
export const buildDocumentConceptAnnotations = (
    document: ExtractedDocument,
    intelligence: DocumentIntelligence,
): ReadonlyMap<string, readonly DocumentConceptTextAnnotation[]> => {
    const domainById = new Map(intelligence.domains.map((domain) => [domain.id, domain]));
    const annotations = new Map<string, DocumentConceptTextAnnotation[]>();

    intelligence.concepts.forEach((concept) => {
        const consumedCandidates = new Set<string>();

        [...concept.references]
            .sort((left, right) => left.page - right.page || left.textStart - right.textStart)
            .forEach((reference) => {
                const stableSourceOffsets = pageHasStableSourceOffsets(document.blocks, reference.page);
                const candidates = getReferenceCandidates(document.blocks, reference)
                    .filter(
                        (candidate) =>
                            !consumedCandidates.has(`${candidate.block.id}:${candidate.start}:${candidate.end}`),
                    )
                    .sort((left, right) => {
                        if (stableSourceOffsets) {
                            const leftDistance = Math.abs(
                                (left.sourceStart ?? Number.MAX_SAFE_INTEGER) - reference.textStart,
                            );
                            const rightDistance = Math.abs(
                                (right.sourceStart ?? Number.MAX_SAFE_INTEGER) - reference.textStart,
                            );

                            if (leftDistance !== rightDistance) {
                                return leftDistance - rightDistance;
                            }
                        }

                        return left.blockIndex - right.blockIndex || left.start - right.start;
                    });
                const candidate = candidates[0];

                if (!candidate) {
                    return;
                }

                consumedCandidates.add(`${candidate.block.id}:${candidate.start}:${candidate.end}`);
                annotations.set(candidate.block.id, [
                    ...(annotations.get(candidate.block.id) ?? []),
                    {
                        blockId: candidate.block.id,
                        concept,
                        domain: concept.primaryDomainId ? domainById.get(concept.primaryDomainId) : undefined,
                        end: candidate.end,
                        reference,
                        start: candidate.start,
                    },
                ]);
            });
    });

    return new Map(
        [...annotations.entries()].map(([blockId, blockAnnotations]) => [
            blockId,
            resolveOverlappingAnnotations(blockAnnotations),
        ]),
    );
};
